import DeleteConfirmDialog from '@/components/ui/dialog/delete-confirm-dialog';
import { pdkAxios } from '@/lib/pdk-axios';
import type { PrimaryCustomer } from '@/models/registry';
import { customerAddressStore } from '@/models/stores';
import type { Address, Deal } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MinusIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Button } from '../../ui/button';

const DeleteDeliveryAddressButton = ({
  dealId,
  customerId,
  deliveryAddressId,
}: {
  dealId: Deal['id'];
  customerId: PrimaryCustomer['id'];
  deliveryAddressId: Address['id'];
}) => {
  const queryClient = useQueryClient();
  const deleteDeliveryAddressMutation = useMutation({
    onMutate: async () => {
      customerAddressStore.remove({
        id: deliveryAddressId,
        customerId,
      });
    },
    mutationFn: async (): Promise<Address> => {
      const response = await pdkAxios
        .post(
          `/deals/${dealId}/primary-customer/${customerId}/delivery-address/${deliveryAddressId}/delete`,
        )
        .catch((e) => {
          console.error('Error deleting delivery address', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      setDialogIsOpen(false);
    },
    onError: (error, variables) => {
      console.error('Error deleting delivery address', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const onOpen = useCallback(() => {
    setDialogIsOpen(true);
  }, [setDialogIsOpen]);
  const onClose = useCallback(() => {
    setDialogIsOpen(false);
  }, [setDialogIsOpen]);
  const onConfirm = () => deleteDeliveryAddressMutation.mutate();

  return (
    <DeleteConfirmDialog
      onConfirm={onConfirm}
      dialogIsOpen={dialogIsOpen}
      onOpen={onOpen}
      onClose={onClose}
      title="Delete Delivery Address"
      description="Please confirm you would like to delete the delivery address."
    >
      <Button variant="ghost" className="p-0 h-fit w-fit text-gray-500">
        <MinusIcon className="w-2 h-2" />
      </Button>
    </DeleteConfirmDialog>
  );
};

export default DeleteDeliveryAddressButton;
